import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import {
  Spin, Input, Select, DatePicker, Row, Col, Space, Button,
  Typography, Table, Tag, Segmented, Empty, Avatar, message,
} from "antd";
import * as XLSX from "xlsx";
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer,
} from "recharts";
import {
  PieChartOutlined, TableOutlined, DownloadOutlined,
  UserOutlined, FilterOutlined, LoginOutlined, LogoutOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, name, value, fill }) => {
  if (percent < 0.03) return null;
  const RADIAN = Math.PI / 180;
  const r = outerRadius + 18;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill={fill} textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central" style={{ fontSize: 12, fontWeight: 700 }}>
      {`${name}: ${value}`}
    </text>
  );
};

function timeToMinutes(t) {
  if (!t) return 99999;
  const m = String(t).match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return 99999;
  let h = parseInt(m[1], 10);
  const mins = parseInt(m[2], 10);
  if (m[3].toUpperCase() === "PM" && h < 12) h += 12;
  if (m[3].toUpperCase() === "AM" && h === 12) h = 0;
  return h * 60 + mins;
}

const TimeCell = ({ val, color, Icon }) => {
  if (!val) return <span style={{ color: "#cbd5e1", fontSize: 13 }}>-</span>;
  return (
    <Tag style={{
      background: `${color}12`, color, border: `1px solid ${color}35`,
      borderRadius: 6, fontWeight: 800, fontSize: 11.5, margin: 0, padding: "2px 8px",
      display: "inline-flex", alignItems: "center", gap: 3,
    }}>
      <Icon style={{ fontSize: 10 }} />{val}
    </Tag>
  );
};

export default function EmployeeAttendance({
  selectedDate: externalDate,
  selectedOrg: externalOrg,
  selectedDept: externalDept,
}) {
  const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };
  const [rawEmployees, setRawEmployees] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [organisationId, setOrganisationId] = useState(externalOrg || "ALL");
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState(externalDept && externalDept !== "ALL" ? externalDept : "");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedDate, setSelectedDate] = useState(externalDate ? dayjs(externalDate, "YYYY-MM-DD") : dayjs());
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("table");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (externalDate) {
      const p = dayjs(externalDate, "YYYY-MM-DD");
      if (p.isValid()) setSelectedDate(p);
    }
  }, [externalDate]);
  useEffect(() => { if (externalOrg !== undefined) setOrganisationId(externalOrg); }, [externalOrg]);
  useEffect(() => { if (externalDept !== undefined) setDepartmentId(externalDept === "ALL" ? "" : externalDept); }, [externalDept]);

  useEffect(() => {
    axios.get(`${backUrl}/api/emp/organisations/`, {
      headers: { Authorization: `Bearer ${authData?.access}` },
    }).then(r => setOrganisations(r.data.results || r.data || [])).catch(() => { });
  }, [backUrl, authData?.access]);

  useEffect(() => {
    const q = organisationId !== "ALL" ? `?organisation=${organisationId}` : "";
    axios.get(`${backUrl}/api/emp/departments/${q}`, {
      headers: { Authorization: `Bearer ${authData?.access}` },
    }).then(r => {
      setDepartments(r.data.results || r.data || []);
      setDepartmentId("");
    }).catch(() => { });
  }, [organisationId, backUrl, authData?.access]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const d = (selectedDate || dayjs()).format("YYYY-MM-DD");
        const params = { from_date: d, to_date: d };
        if (organisationId !== "ALL") params.organisation = organisationId;
        if (departmentId && departmentId !== "ALL") params.department = departmentId;
        const res = await axios.get(`${backUrl}/api/hr/employee-attendance-summary/`, {
          params,
          headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setRawEmployees(Array.isArray(res.data) ? res.data : (res.data?.results || []));
      } catch (err) {
        console.error("Attendance fetch error:", err);
        setRawEmployees([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    setCurrentPage(1);
    // Auto-refresh breakdown live logs every 30 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [organisationId, departmentId, selectedDate, backUrl, authData?.access]);

  // KEY FIX: Leave status is ALWAYS respected even when hasPunch=true.
  // If employee has leave AND punched in -> Half Day (not Present).
  // WeekOff/Sunday only shown when no punch AND no leave.
  const processedEmployees = useMemo(() => {
    return rawEmployees.map((emp) => {
      const att = (Array.isArray(emp.attendance) ? emp.attendance : [])[0] || {};
      const rawStatus = (att.status || "").toUpperCase().trim();
      const fitType = (att.first_in_type || "").toUpperCase();
      const statusMsg = (att.status_message || "").toUpperCase();

      const checkIn = att.check_in || null;
      const checkOut = att.check_out || null;
      const punchIn = att.punch_in || null;
      const punchOut = att.punch_out || null;
      const firstIn = punchIn || checkIn || null;
      const lastOut = punchOut || checkOut || null;
      const hasPunch = !!firstIn;
      const isHalfDay = rawStatus.includes("HALF") || statusMsg.includes("HALF") ||
        (att.status_message || "").toLowerCase().includes("half");

      let sk = "ABSENT", sl = "Absent";
      let ts = { background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" };

      if (rawStatus === "COMPOFF" || rawStatus.includes("COMP OFF") ||
        rawStatus === "CO" || (rawStatus.includes("COMP") && !rawStatus.includes("COMPLETE"))) {
        if (hasPunch || isHalfDay) {
          sk = "HALFCOMPOFF"; sl = "Half Day Comp Off";
          ts = { background: "#66ffff", color: "#0f172a", border: "1px solid #66ffff" };
        } else {
          sk = "COMPOFF"; sl = "Comp Off";
          ts = { background: "#1FA2CC", color: "#fff", border: "1px solid #1FA2CC" };
        }
      } else if (rawStatus === "CASUAL" || rawStatus === "CL" || rawStatus.includes("CASUAL")) {
        if (hasPunch || isHalfDay) {
          sk = "HALFCASUAL"; sl = "Half Day Casual";
          ts = { background: "#FE66CB", color: "#4a0030", border: "1px solid #FE66CB" };
        } else {
          sk = "CASUAL"; sl = "Casual Leave";
          ts = { background: "#CB00CC", color: "#fff", border: "1px solid #CB00CC" };
        }
      } else if (rawStatus === "SICK" || rawStatus === "SL" || rawStatus.includes("SICK")) {
        if (hasPunch || isHalfDay) {
          sk = "HALFSICK"; sl = "Half Day Sick";
          ts = { background: "#B1A1C8", color: "#3b0764", border: "1px solid #B1A1C8" };
        } else {
          sk = "SICK"; sl = "Sick Leave";
          ts = { background: "#FF0066", color: "#fff", border: "1px solid #FF0066" };
        }
      } else if (rawStatus === "EARNED" || rawStatus === "EL" || rawStatus.includes("EARNED")) {
        if (hasPunch || isHalfDay) {
          sk = "HALFEARNED"; sl = "Half Day Earned";
          ts = { background: "#FE66CB", color: "#4a0030", border: "1px solid #FE66CB" };
        } else {
          sk = "EARNED"; sl = "Earned Leave";
          ts = { background: "#1FA2CC", color: "#fff", border: "1px solid #1FA2CC" };
        }
      } else if (rawStatus === "NLB" || rawStatus.includes("LOP") ||
        rawStatus.includes("LOSS OF PAY") || rawStatus.includes("LOSS")) {
        sk = "NLB"; sl = "NLB";
        ts = { background: "#bf4406", color: "#fff", border: "1px solid #bf4406" };
      } else if (rawStatus.includes("HALFDAY") || (isHalfDay && rawStatus.includes("LEAVE"))) {
        sk = "HALFCASUAL"; sl = "Half Day Leave";
        ts = { background: "#FE66CB", color: "#4a0030", border: "1px solid #FE66CB" };
      } else if (rawStatus.includes("LEAVE") && !rawStatus.includes("WEEK")) {
        sk = "LEAVE"; sl = "On Leave";
        ts = { background: "#CB00CC", color: "#fff", border: "1px solid #CB00CC" };
      } else if (rawStatus.includes("PERMISSION")) {
        sk = "PERMISSION"; sl = "Permission";
        ts = { background: "#98FF66", color: "#166534", border: "1px solid #98FF66" };
      } else if (rawStatus === "ABSENT") {
        sk = "ABSENT"; sl = "Absent";
        ts = { background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" };
      } else if (rawStatus.includes("ONDUTY") || rawStatus.includes("ON DUTY")) {
        sk = "ONDUTY"; sl = "On Duty";
        ts = { background: "#6366F1", color: "#fff", border: "1px solid #6366F1" };
      } else if (rawStatus.includes("LATE") || fitType.includes("LATE") || statusMsg.includes("LATE")) {
        sk = "LATE"; sl = "Late In";
        ts = { background: "#FFFECB", color: "#b45309", border: "1px solid #F59E0B" };
      } else if (rawStatus.includes("PRESENT") || (hasPunch && rawStatus !== "WEEKOFF" && rawStatus !== "HOLIDAY")) {
        sk = "PRESENT"; sl = "Present";
        ts = { background: "#00AF50", color: "#fff", border: "1px solid #00AF50" };
      } else if (rawStatus === "HOLIDAY" || (att.is_holiday && (rawStatus === "ABSENT" || !rawStatus))) {
        sk = "HOLIDAY"; sl = "Holiday";
        ts = { background: "#F79647", color: "#fff", border: "1px solid #F79647" };
      } else if (rawStatus.includes("WEEKOFF") || rawStatus.includes("WEEK OFF") ||
        rawStatus === "WO" || rawStatus === "SUNDAY" || (att.is_sunday && (rawStatus === "ABSENT" || !rawStatus))) {
        sk = "WEEKOFF"; sl = "Week Off";
        ts = { background: "#FFFF00", color: "#78350f", border: "1px solid #FFFF00" };
      } else {
        sk = "ABSENT"; sl = "Absent";
        ts = { background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" };
      }

      return {
        id: emp.employee_id,
        code: emp.employee_code || "-",
        name: emp.employee_name || "Employee",
        photo: emp.photo,
        department: emp.department || "-",
        designation: emp.designation || "-",
        organisation: emp.organisation || "-",
        statusKey: sk, statusLabel: sl, tagStyle: ts,
        checkIn, punchIn, checkOut, punchOut,
        firstIn, lastOut,
        totalTime: att.total_time || "-",
      };
    });
  }, [rawEmployees]);

  const HALF_STATUSES = ["HALFCASUAL", "HALFSICK", "HALFEARNED", "HALFCOMPOFF"];
  const LEAVE_STATUSES = ["LEAVE", "CASUAL", "SICK", "COMPOFF", "EARNED", "NLB", ...HALF_STATUSES];
  const count = (key) => processedEmployees.filter(e => e.statusKey === key).length;
  const totalCount = processedEmployees.length;
  const presentCount = count("PRESENT");
  const lateCount = count("LATE");
  const ondutyCount = count("ONDUTY");
  const compoffCount = count("COMPOFF");
  const halfCompoffCount = count("HALFCOMPOFF");
  const casualCount = count("CASUAL");
  const halfCasualCount = count("HALFCASUAL");
  const sickCount = count("SICK");
  const halfSickCount = count("HALFSICK");
  const earnedCount = count("EARNED");
  const halfEarnedCount = count("HALFEARNED");
  const permissionCount = count("PERMISSION");
  const absentCount = count("ABSENT");
  const weekoffCount = count("WEEKOFF");
  const holidayCount = count("HOLIDAY");
  const leaveCount = processedEmployees.filter(e => LEAVE_STATUSES.includes(e.statusKey)).length;
  const halfDayCount = processedEmployees.filter(e => HALF_STATUSES.includes(e.statusKey)).length;

  const filteredEmployees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = processedEmployees.filter(emp => {
      const matchSearch = !q ||
        (emp.name || "").toLowerCase().includes(q) ||
        (emp.code || "").toLowerCase().includes(q) ||
        (emp.department || "").toLowerCase().includes(q) ||
        (emp.designation || "").toLowerCase().includes(q);
      const matchStatus =
        selectedStatus === "ALL" ||
        emp.statusKey === selectedStatus ||
        (selectedStatus === "LEAVE" && LEAVE_STATUSES.includes(emp.statusKey)) ||
        (selectedStatus === "HALFDAY" && HALF_STATUSES.includes(emp.statusKey)) ||
        (selectedStatus === "COMPOFF" && (emp.statusKey === "COMPOFF" || emp.statusKey === "HALFCOMPOFF"));
      return matchSearch && matchStatus;
    });
    // Sort: employees with real check-in come FIRST (earliest → latest)
    // Those without any check-in (absent/leave/weekoff) go to BOTTOM alphabetically
    list.sort((a, b) => {
      const mA = timeToMinutes(a.firstIn);
      const mB = timeToMinutes(b.firstIn);
      const hasA = mA < 99999;
      const hasB = mB < 99999;
      // Both have check-in → sort by time
      if (hasA && hasB) return mA !== mB ? mA - mB : (a.name || "").localeCompare(b.name || "");
      // Only A has check-in → A comes first
      if (hasA && !hasB) return -1;
      // Only B has check-in → B comes first
      if (!hasA && hasB) return 1;
      // Neither has check-in → sort alphabetically
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [processedEmployees, searchTerm, selectedStatus]);

  // Pie chart uses ALL processedEmployees (not filtered) for true distribution with exact Calendar colors
  const pieData = useMemo(() => [
    { name: "Present", value: presentCount, fill: "#00AF50" },
    { name: "Late In", value: lateCount, fill: "#F59E0B" },
    { name: "On Duty", value: ondutyCount, fill: "#6366F1" },
    { name: "Casual Leave", value: casualCount, fill: "#CB00CC" },
    { name: "Half Day Casual", value: halfCasualCount, fill: "#FE66CB" },
    { name: "Sick Leave", value: sickCount, fill: "#FF0066" },
    { name: "Half Day Sick", value: halfSickCount, fill: "#B1A1C8" },
    { name: "Comp Off", value: compoffCount, fill: "#1FA2CC" },
    { name: "Half Day Comp Off", value: halfCompoffCount, fill: "#66ffff" },
    { name: "Earned Leave", value: earnedCount, fill: "#1FA2CC" },
    { name: "Half Day Earned", value: halfEarnedCount, fill: "#FE66CB" },
    { name: "NLB", value: count("NLB"), fill: "#bf4406" },
    { name: "Permission", value: permissionCount, fill: "#98FF66" },
    { name: "Absent", value: absentCount, fill: "#FE0000" },
    { name: "Week Off", value: weekoffCount, fill: "#FFFF00" },
    { name: "Holiday", value: holidayCount, fill: "#F79647" },
  ].filter(i => i.value > 0), [presentCount, lateCount, ondutyCount, halfCasualCount, halfSickCount,
    halfCompoffCount, halfEarnedCount, compoffCount, casualCount, sickCount, earnedCount,
    permissionCount, absentCount, weekoffCount, holidayCount]);

  const columns = [
    {
      title: "#", key: "idx", width: 40, fixed: "left",
      render: (_, __, i) => <span style={{ color: "#94a3b8", fontWeight: 700, fontSize: 12 }}>{i + 1}</span>
    },
    {
      title: "Code", dataIndex: "code", key: "code", width: 78, fixed: "left",
      render: v => <Tag style={{ fontWeight: 800, borderRadius: 5, margin: 0, fontSize: 11 }}>{v || "-"}</Tag>
    },
    {
      title: "Employee", dataIndex: "name", key: "name", width: 195, fixed: "left",
      render: (v, r) => (
        <Space size={8}>
          <Avatar size={34} src={r.photo} icon={!r.photo && <UserOutlined />}
            style={{ backgroundColor: "#0e497b", flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 800, color: "#0f172a", fontSize: 13, lineHeight: 1.2 }}>{v}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{r.department}</div>
          </div>
        </Space>
      )
    },
    {
      title: "Designation", dataIndex: "designation", key: "designation", width: 130,
      render: v => <span style={{ color: "#64748b", fontSize: 12 }}>{v}</span>
    },
    {
      title: "Status", dataIndex: "statusLabel", key: "status", width: 130, align: "center",
      render: (v, r) => (
        <Tag style={{ fontWeight: 800, borderRadius: 8, padding: "3px 10px", fontSize: 11.5, ...r.tagStyle }}>{v}</Tag>
      )
    },
    {
      key: "inTime", width: 120, align: "center",
      title: () => (
        <div style={{ textAlign: "center", lineHeight: 1.3 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#15803d" }}><LoginOutlined /> In Time</div>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>(App CI / Bio PI)</div>
        </div>
      ),
      render: (_, r) => <TimeCell val={r.checkIn || r.punchIn} color="#15803d" Icon={LoginOutlined} />
    },
    {
      key: "outTime", width: 120, align: "center",
      title: () => (
        <div style={{ textAlign: "center", lineHeight: 1.3 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#d97706" }}><LogoutOutlined /> Out Time</div>
          <div style={{ fontSize: 10, color: "#94a3b8" }}>(App CO / Bio PO)</div>
        </div>
      ),
      render: (_, r) => <TimeCell val={r.checkOut || r.punchOut} color="#d97706" Icon={LogoutOutlined} />
    },
    {
      title: "Total Hours", dataIndex: "totalTime", key: "totalTime", width: 124, align: "center",
      render: v => (
        <Tag color="blue" style={{ borderRadius: 6, fontWeight: 800, margin: 0, padding: "3px 10px", fontSize: 11.5 }}>
          {v && v !== "-" ? v : "00 Hrs 00 Mins"}
        </Tag>
      )
    },
  ];

  // Export ALL employees (not just filtered) with correct statuses
  const exportExcel = () => {
    if (!processedEmployees.length) { message.warning("No attendance data to export!"); return; }
    const d = (selectedDate || dayjs()).format("YYYY-MM-DD");
    const rows = processedEmployees.map((e, i) => ({
      "Sr.No.": i + 1,
      "Employee Code": e.code || "-",
      "Employee Name": e.name || "-",
      "Company": e.organisation || "-",
      "Department": e.department || "-",
      "Designation": e.designation || "-",
      "Date": d,
      "Status": e.statusLabel || "-",
      "In Time (App CI / Bio PI)": e.checkIn || e.punchIn || "-",
      "Out Time (App CO / Bio PO)": e.checkOut || e.punchOut || "-",
      "Total Hours": e.totalTime && e.totalTime !== "-" ? e.totalTime : "00 Hrs 00 Mins",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 6 }, { wch: 14 }, { wch: 26 }, { wch: 22 }, { wch: 20 },
      { wch: 20 }, { wch: 12 }, { wch: 18 }, { wch: 26 }, { wch: 26 }, { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance Breakdown");
    XLSX.writeFile(wb, `Daily_Attendance_${d}.xlsx`);
    message.success("Excel exported successfully!");
  };

  return (
    <div style={{
      background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
      borderRadius: 16, border: "1px solid #e2e8f0",
      boxShadow: "0 4px 16px rgba(15,23,42,0.04)", overflow: "hidden",
    }}>
      <div style={{ height: 3.5, background: "linear-gradient(90deg,#0e497b,#3b82f6,#10b981)" }} />

      <div style={{ padding: "18px 20px" }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 14 }} gutter={[12, 12]}>
          <Col>
            <Title level={4} style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>
              Employee Daily Attendance Breakdown
            </Title>
            <Text style={{ color: "#64748b", fontSize: 12.5 }}>
              Live attendance &middot; In Time / Out Time &middot; Status &middot; Hours
            </Text>
          </Col>
          <Col>
            <Space size="middle" wrap>
              <Segmented
                options={[
                  { label: "Table", value: "table", icon: <TableOutlined /> },
                  { label: "Pie Chart", value: "pie", icon: <PieChartOutlined /> },
                ]}
                value={viewMode} onChange={v => setViewMode(v)}
              />
              <Button icon={<DownloadOutlined />} onClick={exportExcel}
                style={{ borderRadius: 8, background: "#f1f5f9", borderColor: "#cbd5e1", fontWeight: 700 }}>
                Export Excel
              </Button>
            </Space>
          </Col>
        </Row>

        <Row gutter={[10, 10]} align="middle" style={{ marginBottom: 14 }}>
          <Col xs={24} sm={12} md={4}>
            <DatePicker allowClear={false} value={selectedDate}
              onChange={v => setSelectedDate(v || dayjs())} style={{ width: "100%", borderRadius: 8 }} />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select value={organisationId} onChange={v => setOrganisationId(v || "ALL")}
              allowClear onClear={() => setOrganisationId("ALL")} style={{ width: "100%" }}>
              <Option value="ALL">All Organisations ({organisations.length})</Option>
              {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select placeholder="Filter Department" value={departmentId || "ALL"}
              onChange={v => setDepartmentId(v === "ALL" ? "" : (v || ""))}
              allowClear onClear={() => setDepartmentId("")} style={{ width: "100%" }}>
              <Option value="ALL">All Departments ({departments.length})</Option>
              {departments.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select value={selectedStatus} onChange={v => { setSelectedStatus(v || "ALL"); setCurrentPage(1); }}
              allowClear onClear={() => setSelectedStatus("ALL")}
              style={{ width: "100%" }} suffixIcon={<FilterOutlined />}>
              <Option value="ALL">All Statuses ({totalCount})</Option>
              <Option value="PRESENT">Present ({presentCount})</Option>
              <Option value="LATE">Late In ({lateCount})</Option>
              {ondutyCount > 0 && <Option value="ONDUTY">On Duty ({ondutyCount})</Option>}
              {halfDayCount > 0 && <Option value="HALFDAY">Half Day ({halfDayCount})</Option>}
              <Option value="LEAVE">All Leaves ({leaveCount})</Option>
              {compoffCount > 0 && <Option value="COMPOFF">Comp Off ({compoffCount})</Option>}
              {halfCompoffCount > 0 && <Option value="HALFCOMPOFF">Half Day Comp Off ({halfCompoffCount})</Option>}
              {casualCount > 0 && <Option value="CASUAL">Casual Leave ({casualCount})</Option>}
              {halfCasualCount > 0 && <Option value="HALFCASUAL">Half Day Casual ({halfCasualCount})</Option>}
              {sickCount > 0 && <Option value="SICK">Sick Leave ({sickCount})</Option>}
              {halfSickCount > 0 && <Option value="HALFSICK">Half Day Sick ({halfSickCount})</Option>}
              {earnedCount > 0 && <Option value="EARNED">Earned Leave ({earnedCount})</Option>}
              {halfEarnedCount > 0 && <Option value="HALFEARNED">Half Day Earned ({halfEarnedCount})</Option>}
              <Option value="PERMISSION">Permission ({permissionCount})</Option>
              <Option value="ABSENT">Absent ({absentCount})</Option>
              {weekoffCount > 0 && <Option value="WEEKOFF">Week Off ({weekoffCount})</Option>}
              {holidayCount > 0 && <Option value="HOLIDAY">Holiday ({holidayCount})</Option>}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Search placeholder="Search Employee / Code / Department..."
              allowClear onSearch={v => setSearchTerm(v)}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              style={{ width: "100%" }} />
          </Col>
        </Row>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {[
            { l: "Total", v: totalCount, c: "blue", key: "ALL" },
            { l: "Present", v: presentCount, c: "green", key: "PRESENT" },
            { l: "Late", v: lateCount, c: "warning", key: "LATE" },
            ...(ondutyCount > 0 ? [{ l: "On Duty", v: ondutyCount, c: "cyan", key: "ONDUTY" }] : []),
            ...(halfDayCount > 0 ? [{ l: "Half Day", v: halfDayCount, bg: "#FE66CB", tc: "#4a0030", key: "HALFDAY" }] : []),
            { l: "On Leave", v: leaveCount, c: "error", key: "LEAVE" },
            ...(compoffCount > 0 || halfCompoffCount > 0 ? [{ l: "Comp Off", v: compoffCount + halfCompoffCount, bg: "#1FA2CC", tc: "#fff", key: "COMPOFF" }] : []),
            { l: "Permission", v: permissionCount, c: "geekblue", key: "PERMISSION" },
            { l: "Absent", v: absentCount, c: "default", key: "ABSENT" },
            ...(weekoffCount > 0 ? [{ l: "Week Off", v: weekoffCount, bg: "#FFFF00", tc: "#78350f", key: "WEEKOFF" }] : []),
            ...(holidayCount > 0 ? [{ l: "Holiday", v: holidayCount, bg: "#F79647", tc: "#fff", key: "HOLIDAY" }] : []),
          ].map(s => (
            <Tag
              key={s.l}
              color={s.bg ? undefined : s.c}
              onClick={() => { setSelectedStatus(s.key); setCurrentPage(1); }}
              style={{
                borderRadius: 8,
                fontWeight: selectedStatus === s.key ? 800 : 700,
                padding: "3px 12px",
                background: s.bg,
                color: s.tc,
                border: selectedStatus === s.key ? "2px solid #0f172a" : (s.bg ? `1px solid ${s.bg}` : undefined),
                cursor: "pointer",
                boxShadow: selectedStatus === s.key ? "0 2px 8px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.04)",
                transform: selectedStatus === s.key ? "translateY(-1px)" : "none",
                transition: "all 0.18s ease"
              }}
            >
              {s.l}: {s.v}
            </Tag>
          ))}
          {filteredEmployees.length !== totalCount && (
            <Tag style={{ borderRadius: 8, fontWeight: 800, padding: "3px 12px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
              Showing {filteredEmployees.length} of {totalCount}
            </Tag>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading Daily Attendance Breakdown...</div>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <Empty description="No employee attendance logs found for selected date and filters" />
          </div>
        ) : viewMode === "pie" ? (
          <Row align="middle" justify="center" gutter={[24, 24]} style={{ padding: "10px 0" }}>
            <Col xs={24} md={12}>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" innerRadius={70} outerRadius={110}
                    paddingAngle={3} label={renderCustomizedLabel}>
                    {pieData.map((e, i) => <Cell key={i} fill={e.fill} stroke="#fff" strokeWidth={2} />)}
                  </Pie>
                  <RTooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0];
                    const pct = totalCount > 0 ? ((d.value / totalCount) * 100).toFixed(1) : 0;
                    return (
                      <div style={{ background: "#fff", border: `1.5px solid ${d.payload.fill}`, borderRadius: 10, padding: "8px 14px", boxShadow: "0 4px 14px rgba(0,0,0,0.1)" }}>
                        <div style={{ fontWeight: 800, color: d.payload.fill, fontSize: 13 }}>{d.name}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginTop: 2 }}>{d.value} employees ({pct}%)</div>
                      </div>
                    );
                  }} />
                </PieChart>
              </ResponsiveContainer>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, paddingRight: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a", marginBottom: 4 }}>
                  Attendance Distribution
                  <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginLeft: 6 }}>
                    (Total: {totalCount})
                  </span>
                </div>
                {pieData.map(item => {
                  const pct = totalCount > 0 ? ((item.value / totalCount) * 100).toFixed(1) : 0;
                  return (
                    <div key={item.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 14px", borderRadius: 10, background: "#f8fafc", border: `1px solid ${item.fill}30` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 11, height: 11, borderRadius: "50%", background: item.fill, flexShrink: 0 }} />
                        <span style={{ fontWeight: 700, fontSize: 13, color: "#334155" }}>{item.name}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>{item.value}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", minWidth: 45, textAlign: "right" }}>({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        ) : (
          <Table
            dataSource={filteredEmployees}
            columns={columns}
            rowKey={r => r.id || r.code}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
              onShowSizeChange: (_, size) => {
                setCurrentPage(1);
                setPageSize(size);
              },
              showTotal: (total, range) => (
                <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                  {range[0]}-{range[1]} of {total} employees (sorted by earliest check-in)
                </span>
              ),
            }}
            size="small"
            scroll={{ x: 1000, y: 480 }}
            style={{ marginTop: 4 }}
          />
        )}
      </div>
    </div>
  );
}
